import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Users, UserPlus, Edit, Trash2, Building, MapPin } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { PEOOrganizationService } from '@/services/peo-organization.service';
import { ROLE_TYPE_LABELS } from '@/types/peo-organization.types';
import { TeamMemberDialog } from './TeamMemberDialog';
import { RoleAssignmentDialog } from './RoleAssignmentDialog';

interface TeamStructureManagerProps {
  providerId: string;
  searchTerm: string;
  filterDepartment: string;
}

export const TeamStructureManager: React.FC<TeamStructureManagerProps> = ({
  providerId,
  searchTerm,
  filterDepartment
}) => {
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);

  const { data: representatives, isLoading } = useQuery({
    queryKey: ['enhanced-representatives', providerId],
    queryFn: () => PEOOrganizationService.getEnhancedRepresentatives(providerId),
    enabled: !!providerId
  });

  const filteredRepresentatives = representatives?.filter(rep => {
    const matchesSearch = 
      rep.profile?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rep.profile?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rep.profile?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rep.team_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesDepartment = 
      filterDepartment === 'all' || rep.department === filterDepartment;

    return matchesSearch && matchesDepartment;
  }) || [];

  const getInitials = (firstName?: string, lastName?: string) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '??';
  };

  const getFullName = (firstName?: string, lastName?: string) => {
    return `${firstName || ''} ${lastName || ''}`.trim() || 'Unknown User';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Team Structure</h2>
          <p className="text-muted-foreground">
            Manage team members, their roles, and reporting relationships.
          </p>
        </div>
        <TeamMemberDialog providerId={providerId}>
          <Button className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            Add Team Member
          </Button>
        </TeamMemberDialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-muted rounded-full"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-muted rounded w-24"></div>
                    <div className="h-3 bg-muted rounded w-16"></div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="h-4 bg-muted rounded"></div>
                  <div className="h-4 bg-muted rounded w-2/3"></div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : filteredRepresentatives.length === 0 ? (
          <div className="col-span-full">
            <Card>
              <CardContent className="py-8 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No team members found</p>
                {searchTerm && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Try adjusting your search term or filters
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          filteredRepresentatives.map((rep) => (
            <Card 
              key={rep.id} 
              className={`hover:shadow-lg transition-shadow cursor-pointer ${
                selectedMemberId === rep.user_id ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => setSelectedMemberId(rep.user_id)}
            >
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarImage src="" />
                    <AvatarFallback>
                      {getInitials(rep.profile?.first_name, rep.profile?.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base truncate">
                      {getFullName(rep.profile?.first_name, rep.profile?.last_name)}
                    </CardTitle>
                    <CardDescription className="truncate">
                      {rep.profile?.email}
                    </CardDescription>
                  </div>
                  <Badge 
                    variant={rep.verification_status === 'verified' ? 'default' : 'secondary'}
                    className="text-xs"
                  >
                    {rep.verification_status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Role:</span>
                    <Badge variant="outline">
                      {rep.organizational_role?.role_name || 'No Role Assigned'}
                    </Badge>
                  </div>
                  
                  {rep.department && (
                    <div className="flex items-center gap-2 text-sm">
                      <Building className="h-3 w-3 text-muted-foreground" />
                      <span>{rep.department}</span>
                    </div>
                  )}
                  
                  {rep.region && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-3 w-3 text-muted-foreground" />
                      <span>{rep.region}</span>
                    </div>
                  )}
                  
                  {rep.team_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="h-3 w-3 text-muted-foreground" />
                      <span>{rep.team_name}</span>
                    </div>
                  )}
                </div>

                {rep.manager_profile && (
                  <div className="border-t pt-3">
                    <p className="text-xs text-muted-foreground mb-1">Reports to:</p>
                    <div className="flex items-center gap-2">
                      <Avatar className="w-6 h-6">
                        <AvatarFallback className="text-xs">
                          {getInitials(rep.manager_profile.first_name, rep.manager_profile.last_name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm">
                        {getFullName(rep.manager_profile.first_name, rep.manager_profile.last_name)}
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <TeamMemberDialog providerId={providerId} member={rep}>
                    <Button variant="outline" size="sm" className="flex-1">
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                  </TeamMemberDialog>
                  <RoleAssignmentDialog 
                    providerId={providerId}
                    selectedUserId={rep.user_id}
                  >
                    <Button variant="outline" size="sm" className="flex-1">
                      <Users className="h-3 w-3 mr-1" />
                      Assign Role
                    </Button>
                  </RoleAssignmentDialog>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};